import { forwardRef, PropsWithChildren, ReactNode } from "react"
import { Theme, styled, cardActionsClasses, cardHeaderClasses, cardContentClasses, SxProps, Box, Typography, cardClasses } from "@mui/material"
import { Icon } from "@tm/components"
import { WidgetCover } from "./WidgetCover"
import { SimpleCard } from "../../muiStyled/Surfaces/simpleCard"

const StyledSimpleCard = styled(SimpleCard, {
    name: "SimpleWidgetCard",
    slot: "root",
    shouldForwardProp: (prop) => prop !== "widgetVariant" && prop !== "variableHeight",
    overridesResolver: (_, styles) => styles.root,
})<{ widgetVariant: "startpage" | "dashboard"; variableHeight: boolean | undefined }>(({ theme, widgetVariant, variableHeight }) => ({
    [`.${cardClasses.root}`]: {
        [`.${cardHeaderClasses.root}`]: {
            ...(widgetVariant === "startpage" && {
                padding: 0,
                marginBottom: "0.2rem",
            }),
        },
        [`.${cardContentClasses.root}`]: {
            display: "flex",
            flexDirection: "column",
            gap: "16px",
            paddingBottom: ".3rem",
            height: "100%",
            ...(widgetVariant === "startpage" && {
                padding: "1em 0 .3rem",
            }),
        },
        [`.${cardActionsClasses.root}`]: {
            ...(widgetVariant === "startpage" && {
                padding: 0,
            }),
            ...(widgetVariant === "dashboard" && {
                padding: "0 1em 1em",
            }),
        },
        ...(widgetVariant === "dashboard" && {
            backgroundColor: theme.colors?.paperBackground || "hsla(0,0%,96.5%,.5)",
            boxShadow: "rgba(0, 0, 0, 0.13) 0px 1px 5px 0px",
            ":hover": {
                borderColor: theme.palette.primary.main,
            },
        }),
        ...(widgetVariant === "startpage" && {
            boxShadow: "unset",
        }),
        height: "100%",
        backgroundSize: "cover",
        display: "flex",
        flexDirection: "column",
        borderRadius: theme.radius?.default || "3px",
    },
    width: "100%",
    height: variableHeight ? "auto" : "100%",
    padding: widgetVariant === "startpage" ? "0px" : ".5em .75em",
}))

type Props = {
    widgetVariant: "startpage" | "dashboard"
    active?: boolean
    title?: string
    icon?: string
    sx?: SxProps<Theme>
    sxHeader?: SxProps<Theme>
    headerAction?: ReactNode
    footerAction?: ReactNode
    className?: string
    handleCoverClick?: () => void
    isVariableHeight?: boolean
}

const WidgetCoverStyles: SxProps<Theme> = {
    [`&& .${cardHeaderClasses.root}`]: {
        padding: "0px",
        margin: "0px",
        borderBottom: "0px",
        [`.${cardHeaderClasses.content}`]: {
            padding: "0px",
        },
    },
    [`&& .${cardContentClasses.root}`]: {
        alignItems: "center",
        justifyContent: "center",
        padding: "0px",
        "&:last-child": { padding: "0px" },
    },
}

export const SimpleWidgetCard = forwardRef(function SimpleWidgetCard(props: PropsWithChildren<Props>, ref) {
    const {
        active = true,
        title,
        children,
        icon,
        sx,
        sxHeader,
        headerAction,
        footerAction,
        handleCoverClick,
        widgetVariant,
        isVariableHeight,
        className,
    } = props

    function renderTitle() {
        return (
            <Box display="flex" alignItems="center" gap={1}>
                {icon && <Icon name={icon} style={{ fontSize: "calc(1em + 2px)" }} />}
                {title && (
                    <Typography variant="body2" color="inherit">
                        {title}
                    </Typography>
                )}
            </Box>
        )
    }

    return (
        <StyledSimpleCard
            title={active ? renderTitle() : undefined}
            ref={ref}
            sx={active ? { ...sx } : { ...WidgetCoverStyles }}
            sxHeader={active ? { ...sxHeader } : { padding: 0 }}
            headerAction={active ? headerAction : undefined}
            footerAction={active ? footerAction : undefined}
            widgetVariant={widgetVariant}
            variableHeight={isVariableHeight}
            className={className}
        >
            {active ? children : <WidgetCover title={title} icon={icon} onCoverClick={handleCoverClick} sx={{ ...sx }} />}
        </StyledSimpleCard>
    )
})
